import React from 'react';
import { Box, Typography } from '@mui/material';
import { TrendPoint } from '../../services/progressService';

/**
 * Score trend as inline SVG (part of feature #12).
 *
 * Hand-drawn rather than pulled from a charting library: this is one line over a handful
 * of points, and the smallest chart dependency would outweigh the whole feature in bundle
 * size. It also keeps the app's dependency list unchanged.
 *
 * Points without a percentage are dropped, not plotted as zero — an unscored attempt is
 * missing data, and a line dipping to the axis would read as a catastrophic result.
 */

interface Props {
  points: TrendPoint[];
  height?: number;
}

const PAD_X = 34;
const PAD_Y = 16;
const WIDTH = 640;

const TrendChart: React.FC<Props> = ({ points, height = 200 }) => {
  const plotted = points.filter((p) => p.percentage != null);

  if (plotted.length < 2) return null;

  const innerW = WIDTH - PAD_X * 2;
  const innerH = height - PAD_Y * 2;

  // Always scale 0–100: a trend from 61% to 64% on an auto-scaled axis looks like a
  // transformation, which would be a flattering lie.
  const x = (i: number) => PAD_X + (innerW * i) / (plotted.length - 1);
  const y = (pct: number) => PAD_Y + innerH - (innerH * pct) / 100;

  const line = plotted
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.percentage!).toFixed(1)}`)
    .join(' ');

  const area =
    `${line} L ${x(plotted.length - 1).toFixed(1)} ${PAD_Y + innerH} ` +
    `L ${x(0).toFixed(1)} ${PAD_Y + innerH} Z`;

  const first = plotted[0].percentage!;
  const last = plotted[plotted.length - 1].percentage!;
  const improving = last >= first;
  const stroke = improving ? '#2f7d4f' : '#b23a3a';

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`Score trend across ${plotted.length} attempts, from ${first.toFixed(
          1
        )}% to ${last.toFixed(1)}%`}
      >
        {[0, 25, 50, 75, 100].map((tick) => (
          <g key={tick}>
            <line
              x1={PAD_X}
              x2={WIDTH - PAD_X}
              y1={y(tick)}
              y2={y(tick)}
              stroke="#e7eae4"
              strokeWidth={1}
            />
            <text x={4} y={y(tick) + 4} fontSize={11} fill="#6a7079">
              {tick}
            </text>
          </g>
        ))}

        <path d={area} fill={stroke} opacity={0.08} />
        <path d={line} fill="none" stroke={stroke} strokeWidth={2.5} strokeLinejoin="round" />

        {plotted.map((p, i) => (
          <g key={p.sessionId ?? i}>
            <circle cx={x(i)} cy={y(p.percentage!)} r={4} fill="#fff" stroke={stroke} strokeWidth={2}>
              <title>
                {`${p.percentage!.toFixed(1)}% · ${new Date(p.takenAt).toLocaleDateString()}`}
              </title>
            </circle>
          </g>
        ))}
      </svg>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {new Date(plotted[0].takenAt).toLocaleDateString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(plotted[plotted.length - 1].takenAt).toLocaleDateString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default TrendChart;
